/**
 * Software Proposal Template Form
 * Project proposal with scope, timeline, and costs
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const SoftwareProposalForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', client_name: '', date: new Date().toISOString().split('T')[0], prepared_by: '', version: '1.0',
    executive_summary: '', problem_statement: '', proposed_solution: '', objectives: [''],
    scope_included: [''], scope_excluded: [''],
    deliverables: [{ name: '', description: '', acceptance_criteria: '' }],
    phases: [{ phase: '', description: '', duration: '', start_date: '', end_date: '', deliverables: '' }],
    team_structure: [{ role: '', responsibilities: '', allocation: '' }],
    technology_stack: [''], assumptions: [''], constraints: [''], risks: [{ risk: '', impact: '', mitigation: '' }],
    cost_breakdown: [{ item: '', description: '', cost: '', type: 'One-time' }],
    total_cost: '', payment_terms: '', warranty_period: '', support_terms: '',
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'Customer Portal Development',
    client_name: 'Acme Corporation',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'TechCorp Solutions',
    version: '1.0',
    executive_summary: 'This proposal outlines the development of a modern customer portal that will enable Acme Corporation customers to manage their accounts, view orders, submit support tickets, and access self-service resources. The portal will integrate with existing CRM and ERP systems.',
    problem_statement: 'Currently, customers must contact support for basic account inquiries, leading to high support costs and poor customer experience. There is no self-service capability for order tracking, invoice viewing, or support ticket management.',
    proposed_solution: 'A responsive web-based customer portal built with React frontend and Node.js backend, featuring SSO integration, real-time order tracking, interactive dashboards, and integrated support ticketing.',
    objectives: [
      'Reduce support call volume by 40% within 6 months of launch',
      'Improve customer satisfaction score (CSAT) from 72% to 85%',
      'Enable 24/7 self-service access for 10,000+ customers',
      'Integrate with Salesforce CRM and SAP ERP systems'
    ],
    scope_included: [
      'Customer authentication with SSO (SAML/OIDC)',
      'Account dashboard with order history and status',
      'Invoice viewing and download',
      'Support ticket creation and tracking',
      'Knowledge base integration',
      'Mobile-responsive design',
      'Admin panel for content management'
    ],
    scope_excluded: [
      'Payment processing (handled by existing system)',
      'Mobile native applications',
      'Legacy system migration',
      'Data cleanup and preparation'
    ],
    deliverables: [
      { name: 'Requirements Document', description: 'Detailed functional and technical requirements', acceptance_criteria: 'Sign-off from stakeholders' },
      { name: 'UI/UX Design', description: 'Wireframes, mockups, and design system', acceptance_criteria: 'Design approval' },
      { name: 'Customer Portal Application', description: 'Fully functional web application', acceptance_criteria: 'All test cases pass, UAT complete' },
      { name: 'API Integration Layer', description: 'REST APIs for CRM and ERP integration', acceptance_criteria: 'API documentation, integration tests pass' },
      { name: 'Admin Dashboard', description: 'Content and user management interface', acceptance_criteria: 'Admin UAT complete' },
      { name: 'Documentation', description: 'User guides, admin guides, technical docs', acceptance_criteria: 'Documentation review complete' }
    ],
    phases: [
      { phase: 'Discovery & Planning', description: 'Requirements gathering, architecture design', duration: '3 weeks', start_date: '2025-02-01', end_date: '2025-02-21', deliverables: 'Requirements doc, project plan' },
      { phase: 'Design', description: 'UI/UX design, prototyping', duration: '3 weeks', start_date: '2025-02-24', end_date: '2025-03-14', deliverables: 'Design mockups, style guide' },
      { phase: 'Development Sprint 1', description: 'Core features: Auth, dashboard, orders', duration: '4 weeks', start_date: '2025-03-17', end_date: '2025-04-11', deliverables: 'MVP release' },
      { phase: 'Development Sprint 2', description: 'Support tickets, knowledge base', duration: '3 weeks', start_date: '2025-04-14', end_date: '2025-05-02', deliverables: 'Feature complete' },
      { phase: 'Integration & Testing', description: 'CRM/ERP integration, QA', duration: '3 weeks', start_date: '2025-05-05', end_date: '2025-05-23', deliverables: 'Integrated system' },
      { phase: 'UAT & Launch', description: 'User acceptance, training, go-live', duration: '2 weeks', start_date: '2025-05-26', end_date: '2025-06-06', deliverables: 'Production launch' }
    ],
    team_structure: [
      { role: 'Project Manager', responsibilities: 'Overall delivery, client communication', allocation: '50%' },
      { role: 'Tech Lead', responsibilities: 'Architecture, code review, technical decisions', allocation: '75%' },
      { role: 'Senior Developer (x2)', responsibilities: 'Frontend and backend development', allocation: '100%' },
      { role: 'UI/UX Designer', responsibilities: 'Design, prototyping', allocation: '50%' },
      { role: 'QA Engineer', responsibilities: 'Testing, quality assurance', allocation: '75%' }
    ],
    technology_stack: ['React 18', 'Node.js', 'PostgreSQL', 'Redis', 'AWS (ECS, RDS, S3)', 'Auth0 SSO', 'REST APIs'],
    assumptions: [
      'Client will provide API access to CRM and ERP systems',
      'SSO identity provider is already configured',
      'Client team available for weekly reviews',
      'Test data will be provided within 2 weeks of project start'
    ],
    constraints: [
      'Must comply with SOC 2 security requirements',
      'Go-live date is fixed due to annual customer conference',
      'Budget cannot exceed $250,000'
    ],
    risks: [
      { risk: 'CRM API limitations', impact: 'High', mitigation: 'Early API discovery, fallback options identified' },
      { risk: 'Scope creep', impact: 'Medium', mitigation: 'Strict change control process, documented scope' },
      { risk: 'Resource availability', impact: 'Medium', mitigation: 'Backup resources identified, cross-training' }
    ],
    cost_breakdown: [
      { item: 'Discovery & Planning', description: 'Requirements, architecture', cost: '$25,000', type: 'One-time' },
      { item: 'UI/UX Design', description: 'Design and prototyping', cost: '$20,000', type: 'One-time' },
      { item: 'Development', description: 'Frontend and backend', cost: '$120,000', type: 'One-time' },
      { item: 'Integration', description: 'CRM/ERP integration', cost: '$35,000', type: 'One-time' },
      { item: 'Testing & QA', description: 'Quality assurance', cost: '$25,000', type: 'One-time' },
      { item: 'Project Management', description: 'PM and coordination', cost: '$20,000', type: 'One-time' },
      { item: 'Infrastructure (Annual)', description: 'AWS hosting', cost: '$24,000', type: 'Recurring' }
    ],
    total_cost: '$245,000 (one-time) + $24,000/year (hosting)',
    payment_terms: '30% on signing, 30% at design approval, 30% at UAT, 10% at go-live',
    warranty_period: '90 days post-launch',
    support_terms: 'Optional annual support contract available at $36,000/year',
    rai_assignments: [
      { task: 'Review and approve proposal', responsible: 'Client PM', accountable: 'Client Sponsor', informed: 'Stakeholders', due_date: '2025-01-30', status: 'Pending' }
    ],
    followups: [
      { item: 'Schedule proposal walkthrough meeting', owner: 'Account Manager', due_date: '2025-01-25', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Software Proposal Draft";
      const payload = {
        name: draftName,
        template_type: "Software Proposal",
        template_id: "software-proposal",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "software-proposal", "Software Proposal", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("software-proposal", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/software-proposal`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Software_Proposal_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Software Proposal",
        formData,
        settings: activeSettings || {},
        templateType: "Software Proposal",
        templateId: "software-proposal",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateListItem = (field, index, value) => {
    const items = [...formData[field]];
    items[index] = value;
    setFormData({ ...formData, [field]: items });
  };

  const addListItem = (field, defaultValue = '') => {
    setFormData({ ...formData, [field]: [...formData[field], defaultValue] });
  };

  const removeListItem = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const costTypes = ['One-time', 'Recurring', 'Variable'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Software Proposal</h1><p className="text-sm text-gray-500">Project proposal with scope, timeline, and costs</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Proposal Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Customer Portal Development" /></div>
              <div><Label>Client Name</Label><Input value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})} placeholder="Acme Corp" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Version</Label><Input value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} placeholder="1.0" /></div>
            </div>

            <div><Label>Executive Summary</Label><Textarea value={formData.executive_summary} onChange={(e) => setFormData({...formData, executive_summary: e.target.value})} placeholder="High-level overview of the proposal..." rows={3} /></div>
            <div><Label>Problem Statement</Label><Textarea value={formData.problem_statement} onChange={(e) => setFormData({...formData, problem_statement: e.target.value})} placeholder="What problem does this solve..." rows={3} /></div>
            <div><Label>Proposed Solution</Label><Textarea value={formData.proposed_solution} onChange={(e) => setFormData({...formData, proposed_solution: e.target.value})} placeholder="How will we solve it..." rows={3} /></div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-3 rounded-lg">
                <Label className="font-semibold text-green-800">Scope Included</Label>
                {formData.scope_included.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={item} onChange={(e) => updateListItem('scope_included', idx, e.target.value)} placeholder="Feature/item in scope" className="text-sm" />
                    <Button variant="ghost" size="icon" onClick={() => removeListItem('scope_included', idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addListItem('scope_included', '')} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <Label className="font-semibold text-red-800">Scope Excluded</Label>
                {formData.scope_excluded.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={item} onChange={(e) => updateListItem('scope_excluded', idx, e.target.value)} placeholder="Out of scope" className="text-sm" />
                    <Button variant="ghost" size="icon" onClick={() => removeListItem('scope_excluded', idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addListItem('scope_excluded', '')} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Cost Breakdown</Label>
              <div className="space-y-2 mt-2">
                {formData.cost_breakdown.map((cost, idx) => (
                  <div key={idx} className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-gray-50 p-2 rounded">
                    <Input value={cost.item} onChange={(e) => { const c = [...formData.cost_breakdown]; c[idx].item = e.target.value; setFormData({...formData, cost_breakdown: c}); }} placeholder="Item" className="text-sm" />
                    <div className="md:col-span-2"><Input value={cost.description} onChange={(e) => { const c = [...formData.cost_breakdown]; c[idx].description = e.target.value; setFormData({...formData, cost_breakdown: c}); }} placeholder="Description" className="text-sm" /></div>
                    <Input value={cost.cost} onChange={(e) => { const c = [...formData.cost_breakdown]; c[idx].cost = e.target.value; setFormData({...formData, cost_breakdown: c}); }} placeholder="$25,000" className="text-sm" />
                    <div className="flex gap-1">
                      <Select value={cost.type} onValueChange={(v) => { const c = [...formData.cost_breakdown]; c[idx].type = v; setFormData({...formData, cost_breakdown: c}); }}>
                        <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{costTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" onClick={() => removeListItem('cost_breakdown', idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, cost_breakdown: [...formData.cost_breakdown, { item: '', description: '', cost: '', type: 'One-time' }]})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add Cost Item</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Total Cost</Label><Input value={formData.total_cost} onChange={(e) => setFormData({...formData, total_cost: e.target.value})} placeholder="$245,000 + $24,000/year" /></div>
              <div><Label>Payment Terms</Label><Input value={formData.payment_terms} onChange={(e) => setFormData({...formData, payment_terms: e.target.value})} placeholder="30% signing, 30% design, 30% UAT, 10% launch" /></div>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SoftwareProposalForm;
